import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { format, parseISO } from 'date-fns';
import { TextBlock } from '../../Atoms/Text';
import { describeSchedule, isCalendarDate, ScheduleForm } from './scheduleRule';
import { SchedulePreview } from './useSchedulePreview';

// The api sends calendar dates; parseISO keeps the day, where new Date would read it as UTC midnight.
const formatRunDate = (date: string, startYear: number): string => {
  const day = parseISO(date);
  return format(day, day.getFullYear() === startYear ? 'MMM d' : 'MMM d, yyyy');
};

const runsText = (preview: SchedulePreview, startYear: number, paused: boolean, hasEndDate: boolean): string => {
  // A paused schedule sends nothing until it is resumed, so its dates only hold from then on.
  const label = paused ? 'Next runs once resumed' : 'Next runs';
  if (preview.status === 'failed') return preview.message;
  if (preview.status !== 'ready') return `${label}: …`;
  if (preview.runDates.length === 0) return hasEndDate ? 'No runs before the end date' : 'No upcoming runs';
  return `${label}: ${preview.runDates.map((date) => formatRunDate(date, startYear)).join(', ')}`;
};

export interface ScheduleSummaryProps {
  form: ScheduleForm;
  preview: SchedulePreview;
  paused: boolean;
  hasEndDate: boolean;
}

/** The schedule's cadence in plain words and the next dates the api will send it on. */
export const ScheduleSummary = ({ form, preview, paused, hasEndDate }: ScheduleSummaryProps) => {
  const start = isCalendarDate(form.startDate) ? parseISO(form.startDate) : null;
  // The live region stays mounted while there is no start date, so the first summary after one is set is announced.
  return (
    <div
      aria-live="polite"
      aria-busy={preview.status === 'loading'}>
      {start && (
        <div className="flex items-start gap-2 rounded bg-blue-50 px-3 py-2 mb-3">
          <CalendarTodayOutlinedIcon
            fontSize="small"
            className="text-indigo"
          />
          <TextBlock
            size="sm"
            color="grey9">
            {`${describeSchedule(form)}, starting ${format(start, 'MMM d, yyyy')} · ${runsText(preview, start.getFullYear(), paused, hasEndDate)}`}
          </TextBlock>
        </div>
      )}
    </div>
  );
};
